/**
 * Boot-loop breaker (incident 2026-09-26: one Safari window reloaded the SPA
 * in a loop and flooded the API with ~100k requests).
 *
 * Keeps a ring of recent boot timestamps in sessionStorage. If the page boots
 * too many times within a short window, index.js must not render the app — it
 * shows a static panel instead and makes no network requests.
 *
 * sessionStorage is per-tab, which matches the incident shape (one window
 * reloading itself). Every storage access is guarded: private mode can throw,
 * and a guard that can't record must never lock the user out.
 */

const BOOT_RING_KEY = 'ww_boot_ring';
const WINDOW_MS = 10000;
// More than this many boots inside WINDOW_MS means the 6th boot trips.
const MAX_BOOTS_IN_WINDOW = 5;

function defaultStorage() {
  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
}

/**
 * Record a boot and report whether the loop breaker has tripped.
 *
 * @param {number} now - Current timestamp (injectable for tests)
 * @param {Storage|null} storage - sessionStorage (injectable for tests)
 * @returns {boolean} true when the app must NOT boot
 */
export function checkBootLoop(now = Date.now(), storage = defaultStorage()) {
  if (!storage) {
    return false;
  }

  let ring = [];
  try {
    const parsed = JSON.parse(storage.getItem(BOOT_RING_KEY) || '[]');
    if (Array.isArray(parsed)) {
      ring = parsed;
    }
  } catch (error) {
    ring = [];
  }

  ring = ring.filter((t) => typeof t === 'number' && now - t < WINDOW_MS);
  ring.push(now);

  try {
    storage.setItem(BOOT_RING_KEY, JSON.stringify(ring));
  } catch (error) {
    // Can't persist → never trip (fails open, keeps the app usable)
    return false;
  }

  return ring.length > MAX_BOOTS_IN_WINDOW;
}

/**
 * Clear the boot ring (the "Try again" / "Clear session and reload" buttons).
 */
export function clearBootRing(storage = defaultStorage()) {
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(BOOT_RING_KEY);
  } catch (error) {
    // ignore
  }
}
